import { useQuery } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { parseAsString, useQueryState } from 'nuqs'
import * as React from 'react'

import { useBranding } from '@/hooks/use-branding'
import useLocale from '@/hooks/use-locale'
import { useDebounce } from '@/hooks/useDebounce'

import { BlogCard, BlogSkeleton } from '@/components/cards/blog-card'
import { PageHeader, PageHeaderDescription, PageHeaderHeading } from '@/components/common/page-header'
import { Shell } from '@/components/common/shell'
import PaginationComponent from '@/components/PaginationComponent'
import SEO from '@/components/seo/SEO'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import RootContainer from '@/containers/root-container'

import { useAuth } from '@/contexts/auth-context'
import { Role } from '@/enums/role.enum'
import blogService from '@/services/blog.service'
import { createServerSideApiConfig } from '@/utils/api.utils'

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const { locale, res } = context
  // Set caching: fresh for 10s, then stale for up to 59s
  res?.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59')
  const searchParams = context?.query
  const apiConfig = createServerSideApiConfig(context)
  const listBlog = await blogService.getPublicBlogs(
    {
      pageNumber: 1,
      pageSize: 9,
      search: searchParams?.search ? String(searchParams.search) : '',
      searchFields: ['title', 'content', 'metaDescription'],
      locale,
    },
    apiConfig
  )
  return {
    props: { listBlog, locale },
  }
}

export default function UserBlogsIndexPage({
  listBlog: initialListBlog,
  locale,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const searchParams = useSearchParams()
  const pageIndex = Number(searchParams.get('page')) || 1
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = React.useState('')
  const debouncedValue = useDebounce(inputValue, 500)
  const { user } = useAuth()
  const router = useRouter()
  const branding = useBranding()

  const [searchQuery, setSearchQuery] = useQueryState('search', parseAsString.withDefault(''))
  const [sortBlogBy, setSortBlogBy] = useQueryState('sortBlogBy', parseAsString.withDefault('-createdAt'))

  React.useEffect(() => {
    const trimmedValue = debouncedValue.trim()
    if (trimmedValue !== searchQuery) {
      if (trimmedValue) {
        setSearchQuery(trimmedValue)
      } else {
        setSearchQuery('')
      }
    }
  }, [debouncedValue, searchQuery, setSearchQuery])

  const {
    data: listBlog,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['blogs', pageIndex, sortBlogBy, searchQuery],
    queryFn: () =>
      blogService.getPublicBlogs({
        pageNumber: pageIndex,
        pageSize: 9,
        sort: [sortBlogBy].join(','),
        search: searchQuery,
        searchFields: ['title', 'content', 'metaDescription'],
        locale,
      }),
    initialData: initialListBlog,
    refetchOnWindowFocus: false,
  })

  React.useEffect(() => {
    refetch()
  }, [initialListBlog, refetch])

  return (
    <RootContainer>
      <SEO
        title={`${branding.siteName} Blog`}
        ogType="website"
        section="Blog"
        tags={['blog', 'data science', 'machine learning', branding.siteName]}
        keywords={`${branding.siteName} Blog, data science, machine learning, ${branding.siteName}`}
      />
      <UserBlogsHeading />
      {listBlog.data && (
        <div className="container">
          <div className="flex flex-col gap-4 md:gap-0 sm:flex-row justify-between items-center mb-8">
            <div className="w-full sm:max-w-xl">
              <div className="flex border border-input rounded-md overflow-hidden group focus-within:ring-1 focus-within:ring-primary/40 focus-within:border-primary/40 transition-all">
                <Input
                  type="text"
                  placeholder="Search for..."
                  className="block h-10 pr-4 py-2 border-0 bg-background text-sm"
                  value={inputValue}
                  ref={searchInputRef}
                  onChange={e => {
                    setInputValue(e.currentTarget.value)
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      const searchValue = e.currentTarget.value.trim()
                      setInputValue(searchValue) // Update input value to trigger debounce
                    }
                  }}
                />
                <Button
                  size="default"
                  variant="outline"
                  className="w-full flex-1 sm:w-auto flex items-center gap-1 border-0 border-l rounded-none focus-visible:ring-0 focus-visible:outline-none"
                  onClick={() => {
                    if (searchInputRef.current) {
                      const searchValue = searchInputRef.current.value.trim()
                      setInputValue(searchValue)
                    }
                  }}
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button
                className="w-full sm:w-auto"
                onClick={() => {
                  if (user?.roles.includes(Role.ADMIN)) {
                    router.push('/admin/blogs/create')
                  } else {
                    router.push(user ? '/dashboard/blogs' : '/sign-in?previousPage=/blogs')
                  }
                }}
              >
                <Plus className="h-4 w-4" />
                <span className="">Post a blog</span>
              </Button>
              <Select
                onValueChange={value => {
                  setSortBlogBy(value)
                }}
              >
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="-createdAt">Newest first</SelectItem>
                  <SelectItem value="createdAt">Oldest first</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 my-12">
          {isFetching && <BlogSkeleton />}
          {!isFetching && listBlog?.data?.map(post => <BlogCard key={post.id} post={post} />)}
        </div>
      </div>

      <div className="mt-10 py-6 md:py-12">
        <PaginationComponent currentPage={+pageIndex || 1} pageSize={10} itemCount={listBlog?.meta?.totalItems || 10} />
      </div>
    </RootContainer>
  )
}

function UserBlogsHeading() {
  const { text } = useLocale()

  return (
    <Shell className="gap-0 md:pb-3 lg:pb-12">
      <PageHeader
        as="section"
        className="mx-auto items-center gap-2 py-8 text-center md:py-12 md:pb-6 lg:py-12 lg:pb-6 xl:pt-20"
      >
        <PageHeaderHeading
          className="animate-fade-up lg:leading-[1.2]"
          style={{ animationDelay: '0.20s', animationFillMode: 'both' }}
        >
          {text.BLOG.TITLE}
        </PageHeaderHeading>
        <PageHeaderDescription
          className="animate-fade-up mt-4 max-w-[46.875rem] font-medium text-pretty"
          style={{ animationDelay: '0.30s', animationFillMode: 'both' }}
        >
          {text.BLOG.DESCRIPTION}
        </PageHeaderDescription>
      </PageHeader>
    </Shell>
  )
}
